/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { lazy, Suspense, useState, useCallback, type MouseEvent } from 'react';
import { Button } from '@/components/ui/button';
import { createProductUrl } from '@/lib/product/product-utils';
import type { ShopperSearch } from '@/scapi';
import { useProductTileContext } from './context';

interface QuickAddButtonProps {
    productId: string;
    productName: string;
    productType?: ShopperSearch.schemas['ProductSearchHit']['productType'];
    /** Currently selected color value — pre-seeds the PDP URL when "Buy it Now" is clicked */
    selectedColorValue?: string | null;
    /**
     * Full variant selections (all axes) from the tile's represented variant. Pre-seeds
     * every swatch in the quick-add modal so the modal matches the variant the tile advertises.
     */
    initialVariantSelections?: Record<string, string>;
    /** Custom button label. Defaults to the `product.quickAdd` locale key */
    label?: string;
    /** Called when the modal open state changes — lets the tile keep the button mounted while open. */
    onOpenChange?: (open: boolean) => void;
}

const CartItemModal = lazy(() =>
    import('@/components/cart-item-modal').then((module) => ({ default: module.CartItemModal }))
);

function preloadCartItemModal() {
    void import('@/components/cart-item-modal');
}

/**
 * Quick Add CTA for a product tile. The parent mounts this on tile hover/focus; the modal
 * chunk is prefetched on hover/focus and mounted only after the first click.
 */
export function QuickAddButton({
    productId,
    productName,
    productType,
    selectedColorValue,
    initialVariantSelections,
    label,
    onOpenChange,
}: QuickAddButtonProps) {
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const { navigate, t } = useProductTileContext();

    const resolvedLabel = label ?? t('quickAdd');

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            setOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [onOpenChange]
    );

    const handleBuyItNow = useCallback(() => {
        handleOpenChange(false);
        void navigate(createProductUrl(productId, selectedColorValue ?? null, 'color'));
    }, [handleOpenChange, navigate, productId, selectedColorValue]);

    const handleOpenModal = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setLoaded(true);
            handleOpenChange(true);
        },
        [handleOpenChange]
    );

    return (
        <>
            <Button
                variant="default"
                size="lg"
                className="w-full rounded-none shadow-2xs cursor-pointer"
                aria-label={`${resolvedLabel} ${productName}`}
                onMouseEnter={preloadCartItemModal}
                onFocus={preloadCartItemModal}
                onClick={handleOpenModal}>
                {resolvedLabel}
            </Button>

            {loaded && (
                <Suspense fallback={null}>
                    <CartItemModal
                        productId={productId}
                        productType={productType}
                        open={open}
                        onOpenChange={handleOpenChange}
                        onBuyNow={handleBuyItNow}
                        initialVariantSelections={initialVariantSelections}
                    />
                </Suspense>
            )}
        </>
    );
}
